import { isObject } from "lodash";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

export const useNavigationFeature = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = useParams();

  const viewPortElement = document.querySelector(".viewport");

  const scrollUpPage = async (): Promise<void> => {
    return new Promise((resolve) => {
      if (!viewPortElement) return resolve();
      viewPortElement.scrollTo(0, 0);
      resolve();
    });
  };

  const getVariableParam = (paramName: string) => {
    return new URLSearchParams(window.location.search).get(paramName);
  };

  const replaceQueryParam = (
    newQueryParams: Record<string, string> | string,
  ) => {
    const options = {
      pathname: location.pathname,
      search: isObject(newQueryParams)
        ? `?${createSearchParams(newQueryParams)}`
        : newQueryParams,
    };
    navigate(options, { replace: true });
  };

  const getUrlParam = (paramName: string) => {
    return urlParams[paramName];
  };

  const goto = async (route: string) => {
    await scrollUpPage();
    navigate(route);
  };

  const getPathName = () => {
    return location.pathname;
  };

  const isRoot = () => {
    return getPathName() === "/";
  };

  const scrollTop = () => {
    if (!viewPortElement) return;
    viewPortElement.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const getScroll = () => {
    return viewPortElement?.scrollTop;
  };

  const watchScroll = (
    onScroll: (scroll?: number, progress?: number) => any,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    if (!viewPortElement) return () => {};

    const handleScroll = () => {
      const scrollTop = viewPortElement.scrollTop;
      const scrollHeight = viewPortElement.scrollHeight;
      const percentage = (scrollTop / scrollHeight) * 100;
      onScroll(getScroll(), percentage);
    };

    viewPortElement.addEventListener("scroll", handleScroll);

    return () => {
      viewPortElement.removeEventListener("scroll", handleScroll);
    };
  };

  return {
    goto,
    getVariableParam,
    getUrlParam,
    getPathName,
    scrollTop,
    getScroll,
    watchScroll,
    isRoot,
    replaceQueryParam,
  };
};
